import { SvelteSet } from 'svelte/reactivity';
import * as valibot from 'valibot';
import type { FavoriteStop } from '../lib/favorites';
import { FAVORITES_KEY, isFavoriteStop } from '../lib/favorites';
import type { StorageLike } from '../lib/storage';

/** Alias kept for callers imported before the shared {@link StorageLike} landed. */
export type FavoriteStorage = StorageLike;

/** Schema that accepts any array, so each item can be individually validated below. */
const UnknownArraySchema = valibot.array(valibot.unknown());

/** Reads and filters the persisted favorites list; returns [] on missing / unreadable / non-array payloads. */
/** Keeps the first entry per `extId`, so the list and its key index stay in step. */
function dedupeByExtensionId(favorites: FavoriteStop[]): FavoriteStop[] {
  const seen = new SvelteSet<string>();
  return favorites.filter((favorite) => seen.size < seen.add(favorite.extId).size);
}

function readPersisted(storage: StorageLike): FavoriteStop[] {
  const rawText = storage.getItem(FAVORITES_KEY);
  if (!rawText) return [];
  try {
    const parsed: unknown = JSON.parse(rawText);
    if (!valibot.is(UnknownArraySchema, parsed)) return [];
    return parsed.filter(isFavoriteStop);
  } catch {
    return [];
  }
}

/** Best-effort persistence of `list`; quota / private-mode failures are swallowed. */
function writePersisted(storage: StorageLike, list: FavoriteStop[]): void {
  try {
    storage.setItem(FAVORITES_KEY, JSON.stringify(list));
  } catch {
    /* quota / private mode — accept the loss; next successful write overwrites */
  }
}

/**
 * Reactive favorites list, most-recently-starred first. Reads storage once at
 * construction; every mutation is authoritative in memory and best-effort
 * writes through — quota / private-mode failures are silently swallowed.
 */
export class FavoritesStore {
  #index = new SvelteSet<string>();
  items = $state<FavoriteStop[]>([]);

  constructor(private storage: StorageLike = localStorage) {
    const initial = dedupeByExtensionId(readPersisted(this.storage));
    this.items = initial;
    for (const favorite of initial) this.#index.add(favorite.extId);
  }

  /** True when `extId` is already starred. */
  isStarred(extensionId: string): boolean {
    return this.#index.has(extensionId);
  }

  /**
   * Prepends `stop` (or moves an existing one to the front by `extId`),
   * stamping `starredAt`, and best-effort writes through to storage. A failed
   * write leaves in-memory state authoritative; TypeScript enforces the shape.
   */
  addFavorite(stop: Omit<FavoriteStop, 'starredAt'>, nowMs: number = Date.now()): void {
    const rebuilt: FavoriteStop[] = [{ ...stop, starredAt: nowMs }];
    for (const favorite of this.items) {
      if (favorite.extId === stop.extId) continue;
      rebuilt.push(favorite);
    }
    this.items = rebuilt;
    this.#index.add(stop.extId);
    writePersisted(this.storage, this.items);
  }

  /** Removes `extId` if present; no-op otherwise. */
  remove(extensionId: string): void {
    if (!this.#index.has(extensionId)) return;
    this.items = this.items.filter((favorite) => favorite.extId !== extensionId);
    this.#index.delete(extensionId);
    writePersisted(this.storage, this.items);
  }

  /** Star `stop` iff not already starred; no-op otherwise. */
  starOn(stop: Omit<FavoriteStop, 'starredAt'>, nowMs: number = Date.now()): void {
    if (this.#index.has(stop.extId)) return;
    this.addFavorite(stop, nowMs);
  }

  /** Unstar `extId` iff currently starred; no-op otherwise. */
  starOff(extensionId: string): void {
    this.remove(extensionId);
  }

  /** Stars if absent, unstars if present. Writes through to storage either way. */
  toggle(stop: Omit<FavoriteStop, 'starredAt'>, nowMs: number = Date.now()): void {
    if (this.#index.has(stop.extId)) {
      this.remove(stop.extId);
      return;
    }
    this.addFavorite(stop, nowMs);
  }
}
